"use client";

import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { addChallengeActivity } from "@/lib/activity/addActivityToDB";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { useAppStore } from "@/store/useAppStore";
import { toast } from "react-toastify";

export const BettingModel = () => {
  const { user } = useAuth();
  const {
    selectedBet,
    betAmount,
    userScore,
    userBets,
    setBetAmount,
    setSelectedBet,
    setUserScore,
    setUserBets,
  } = useAppStore();

  const handlePlaceBet = async (market: any, position: string) => {
    try {
      const amount = parseInt(betAmount);
      if (amount > 0 && amount <= userScore) {
        setUserScore(userScore - amount);

        setUserBets([
          ...userBets,
          {
            market,
            position,
            amount,
            timestamp: new Date().toISOString(),
          },
        ]);

        if (user?.id) {
          await updateUserScore(user.id, -amount);
        }
        await addChallengeActivity(amount);
        alert(`Bet placed! ${amount} points on ${position.toUpperCase()}`);
        setSelectedBet(null);
        setBetAmount("");
      }
    } catch (error) {
      console.log("Error handling bet: ", error);
      toast.error("Failed to handle bet");
    }
  };

  if (!selectedBet) {
    return null;
  }
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/20">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">Place Bet</h3>
          <button
            onClick={() => setSelectedBet(null)}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <h4 className="font-semibold text-white mb-2">
            {selectedBet.market.title}
          </h4>
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-gray-300">Betting on:</span>
            <span
              className={`font-bold ${
                selectedBet.position === "yes"
                  ? "text-green-400"
                  : "text-red-400"
              }`}
            >
              {selectedBet.position.toUpperCase()}
            </span>
            <span className="text-gray-300">
              at{" "}
              {Math.round(
                (selectedBet.position === "yes"
                  ? selectedBet.market.yesPrice
                  : selectedBet.market.noPrice) * 100
              )}
              ¢
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Bet Amount (Max: ${userScore})
          </label>
          <input
            type="number"
            value={betAmount}
            onChange={(e) => setBetAmount(e.target.value)}
            placeholder="Enter amount..."
            min="1"
            max={userScore}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-teal"
          />
        </div>

        {betAmount && parseInt(betAmount) > 0 && (
          <div className="mb-4 p-3 bg-white/5 rounded-xl">
            <div className="text-sm text-gray-300">
              <div>Bet Amount: ${betAmount}</div>
              <div>
                Potential Win: $
                {Math.floor(
                  parseInt(betAmount) /
                    (selectedBet.position === "yes"
                      ? selectedBet.market.yesPrice
                      : selectedBet.market.noPrice)
                )}
              </div>
              <div>
                Profit if correct: $
                {Math.floor(
                  parseInt(betAmount) /
                    (selectedBet.position === "yes"
                      ? selectedBet.market.yesPrice
                      : selectedBet.market.noPrice)
                ) - parseInt(betAmount)}
              </div>
            </div>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={() =>
              handlePlaceBet(selectedBet.market, selectedBet.position)
            }
            disabled={
              !betAmount ||
              parseInt(betAmount) <= 0 ||
              parseInt(betAmount) > userScore
            }
            className="flex-1 bg-gradient-to-r from-brand-teal to-brand-amber text-white px-6 py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-all"
          >
            Place Bet
          </button>
          <button
            onClick={() => setSelectedBet(null)}
            className="px-6 py-3 border border-white/20 text-white rounded-xl hover:bg-white/10 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
